import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { getCentralisationInsurerLabel } from 'src/app/_enums/centralisation-insurer.enum';
import { InsuranceType } from 'src/app/_enums/insurance-type.enum';
import {
  getOwnDescriptionEnum,
  getSavingReasonEnumLabel,
  getThirdPillarSituationLabel,
  OwnDescriptionEnum,
  SavingReasonEnum,
  ThirdPillarSituationEnum,
} from 'src/app/_enums/third-pillar.enum';
import { getControl } from 'src/app/_helpers/utils';
import { CENTRALISATION_INSURERS_BY_INSURANCE } from 'src/app/_services/constants/centralisation-insurances';
import { FamilyMember, User } from 'src/app/_types/auth.types';

import { LeadFormBase } from '../lead-form-base';

@Component({
  selector: 'app-lead-third-pillar-form',
  templateUrl: './third-pillar-form.component.html',
  styleUrls: ['./third-pillar-form.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
})
export class ThirdPillarFormComponent
  extends LeadFormBase
  implements OnDestroy, OnInit
{
  @Input()
  formGroup!: FormGroup;

  @Input()
  familyMembers!: FamilyMember[];

  @Input()
  users!: User[];

  memberIdSubscription!: Subscription;

  userIdSubscription!: Subscription;

  alreadyHasInsuranceSubscription!: Subscription;

  situationSubscription!: Subscription;

  constructor(private fb: FormBuilder) {
    super();
  }

  metadataFormGroup: FormGroup = this.fb.group({
    type: InsuranceType.THIRD_PILLAR,
    hasAlreadyThirdPillar: [null],
    sex: [null],
    birthDate: [null],
    situation: [null],
    hasLPPCotisation: [null],
    idealMonthlySaving: [null],
    savingReasons: [null],
    npaNumber: [null],
    ownDescription: [null],
    situationDescription: [null],
    job: [null],
    alreadyThirdPillarDescription: [null],
    alreadyThirdPillarValue: [null],
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.formGroup.removeControl('metadata');
    if (this.metadata) this.metadataFormGroup.patchValue(this.metadata);
    this.formGroup.addControl('metadata', this.metadataFormGroup);

    this.memberIdSubscription = this.formGroup.controls[
      'memberId'
    ].valueChanges.subscribe((value) => {
      this.setMemberInfo(value);
    });

    this.userIdSubscription = this.formGroup.controls[
      'userId'
    ].valueChanges.subscribe((value) => {
      this.setUserInfo(value);
    });

    this.situationSubscription = this.metadataFormGroup.controls[
      'situation'
    ].valueChanges.subscribe((value) => {
      if (value === ThirdPillarSituationEnum.OTHER) {
        this.metadataFormGroup.controls['job'].reset();
      } else if (value) {
        this.metadataFormGroup.controls['situationDescription'].reset();
      }
    });

    this.alreadyHasInsuranceSubscription = this.metadataFormGroup.controls[
      'hasAlreadyThirdPillar'
    ].valueChanges.subscribe((value) => {
      if (!value) {
        this.metadataFormGroup.controls[
          'alreadyThirdPillarDescription'
        ].reset();
        this.metadataFormGroup.controls['alreadyThirdPillarValue'].reset();
      }
    });
  }

  setMemberInfo(familyMemberId: string) {
    if (!this.familyMembers || this.familyMembers.length === 0) return;
    const familyMember = this.familyMembers.find(
      (x) => x.familyMemberId === familyMemberId
    );
    this.metadataFormGroup.controls['birthDate'].setValue(
      new Date(familyMember?.dob!).toLocaleDateString('fr-FR')
    );
    this.metadataFormGroup.controls['sex'].setValue(familyMember?.gender);
  }

  setUserInfo(userId: string) {
    if (!this.users || this.users.length === 0) return;
    const user = this.users.find((x) => x.userId === userId);
    this.metadataFormGroup.controls['npaNumber'].setValue(user?.postCode);
  }

  ThirdPillarSituationEnum = ThirdPillarSituationEnum;

  situationValues = Object.values(ThirdPillarSituationEnum);

  getSituationLabel = getThirdPillarSituationLabel;

  savingReasonValues = Object.values(SavingReasonEnum);

  getSavingReasonLabel = getSavingReasonEnumLabel;

  ownDescriptionValues = Object.values(OwnDescriptionEnum);

  getOwnDescriptionLabel = getOwnDescriptionEnum;

  ngOnDestroy(): void {
    this.situationSubscription.unsubscribe();
    this.memberIdSubscription.unsubscribe();
    if (this.alreadyHasInsuranceSubscription)
      this.alreadyHasInsuranceSubscription.unsubscribe();
  }

  getControl = getControl;

  getCentralisationInsurerLabel = getCentralisationInsurerLabel;

  COMPANIES = CENTRALISATION_INSURERS_BY_INSURANCE[InsuranceType.THIRD_PILLAR];
}
